import * as React from "react";
import { Box, createStyles, makeStyles, Typography } from "@material-ui/core";
import { includes, last, reduce, slice, sortBy } from "lodash";

import { Button, LogInRequiredModal, Modal } from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { AppContext } from "contexts";
import { parseNumber } from "lib/parse";
import { ResaleInfo } from "services/listings/types";
import {
  LIVE_OFFER_STATES,
  NON_FAILED_DEAL_STATES,
} from "services/offers/constants";
import { buildSortedOfferChains } from "services/offers/offersPostProcessor";
import { ListingOffer } from "services/offers/types";
import theme from "theme";

import AAIVerificationModal from "../AAIVerificationModal";
import AcceptOfferModal from "../AcceptOfferModal";
import EditOfferModal from "../EditOfferModal";
import MakeCounterofferModal from "../MakeCounterofferModal";
import MakeOfferModal from "../MakeOfferModal";
import RejectOfferModal from "../RejectOfferModal";
import VerificationModal from "../VerificationModal";
import WithdrawOfferModal from "../WithdrawOfferModal";
import OfferRow from "./OfferRow";
import ProxyRow from "./ProxyRow";

const useStyles = makeStyles(() =>
  createStyles({
    allOffersModal: {
      "& .MuiDialog-paper": {
        margin: 0,
      },
      "& .MuiDialog-paperFullWidth": {
        width: "100%",
      },
    },
    sectionTitle: {
      fontSize: "1.125rem",
      fontWeight: theme.typography.fontWeightBold,
      textTransform: "none",
    },
  }),
);

interface OfferListProps {
  listingId: number;
  displayProxyRow?: boolean;
  displayTitle?: boolean;
  maxOfferChainsToDisplay?: number;
}

const OfferList: React.FC<OfferListProps> = ({
  listingId,
  displayProxyRow,
  displayTitle,
  maxOfferChainsToDisplay,
}) => {
  const classes = useStyles();
  const { asset, listing, proxy, valuation } = useAssetDetailData(listingId);

  const { user } = React.useContext(AppContext);
  const hasBuyListingsPermission =
    user && includes(user.permissionList, "buy_listings");
  const { resale_info } = asset;
  const resaleInfo =
    "last_update" in (resale_info ?? {}) ? (resale_info as ResaleInfo) : null;
  const {
    accept_final_offers,
    buy_now_price,
    is_owner,
    marketplace_median,
    marketplace_median_multiplier,
    marketplace_median_source,
    minimum_price,
    offers,
    proxy_increment_amount,
    royx_take,
    term_expiration,
    hidden,
    state,
  } = listing;
  const buyNowPrice = Number(buy_now_price);
  const marketplaceMedianMultiplier = Number(marketplace_median_multiplier);
  const royxTake = Number(royx_take);
  const isExpired = state === "expired";
  const isAcceptingFinalOffers = state === "accepting_final_offers";

  const {
    dollar_age,
    id: valuationId,
    lifetime_amount,
    ltm,
    track_list,
  } = valuation;

  const [
    isShowAllOffersModalOpen,
    setIsShowAllOffersModalOpen,
  ] = React.useState(false);
  const [isMakeOfferModalOpen, setIsMakeOfferModalOpen] = React.useState(false);
  const [
    isLogInRequiredModalOpen,
    setIsLogInRequiredModalOpen,
  ] = React.useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = React.useState(
    false,
  );

  const [isAcceptOfferModalOpen, setIsAcceptOfferModalOpen] = React.useState(
    false,
  );
  const [
    acceptingOffer,
    setAcceptingOffer,
  ] = React.useState<ListingOffer | null>(null);
  const handleOpenAcceptOfferModal = (offer: ListingOffer) => {
    setAcceptingOffer(offer);
    setIsAcceptOfferModalOpen(true);
  };

  const [isRejectOfferModalOpen, setIsRejectOfferModalOpen] = React.useState(
    false,
  );
  const [
    rejectingOffer,
    setRejectingOffer,
  ] = React.useState<ListingOffer | null>(null);
  const handleOpenRejectOfferModal = (offer: ListingOffer) => {
    setRejectingOffer(offer);
    setIsRejectOfferModalOpen(true);
  };

  const [
    isMakeCounterofferModalOpen,
    setIsMakeCounterofferModalOpen,
  ] = React.useState(false);
  const [
    counteringOffer,
    setCounteringOffer,
  ] = React.useState<ListingOffer | null>(null);
  const handleOpenCounterofferModal = (offer: ListingOffer) => {
    setCounteringOffer(offer);
    setIsMakeCounterofferModalOpen(true);
  };

  const [isEditOfferModalOpen, setIsEditOfferModalOpen] = React.useState(false);
  const [editingOffer, setEditingOffer] = React.useState<ListingOffer | null>(
    null,
  );
  const handleOpenEditOfferModal = (offer: ListingOffer) => {
    setEditingOffer(offer);
    setIsEditOfferModalOpen(true);
  };

  const [
    isWithdrawOfferModalOpen,
    setIsWithdrawOfferModalOpen,
  ] = React.useState(false);
  const [
    withdrawingOffer,
    setWithdrawingOffer,
  ] = React.useState<ListingOffer | null>(null);
  const handleOpenWithdrawOfferModal = (offer: ListingOffer) => {
    setWithdrawingOffer(offer);
    setIsWithdrawOfferModalOpen(true);
  };
  const [
    isAAIVerificationModalOpen,
    setIsAAIVerificationModalOpen,
  ] = React.useState(false);

  const userActiveOffer = offers.filter(
    offer =>
      offer.buyer_id === user?.userId &&
      LIVE_OFFER_STATES.includes(offer.state),
  )[0];

  const offerChains = buildSortedOfferChains(offers);

  const displayOfferChains = !!maxOfferChainsToDisplay
    ? slice(offerChains, 0, maxOfferChainsToDisplay)
    : offerChains;

  const displayedOffersCount = reduce(
    displayOfferChains,
    (acc, cur) => acc + cur.offerChain.length,
    0,
  );
  const buyerOffers = offers.filter(
    offer =>
      offer.kind !== "seller_counteroffer" ||
      (offer.kind === "seller_counteroffer" &&
        includes(NON_FAILED_DEAL_STATES, offer.state)),
  );
  const sellerCounteroffer = offers.filter(
    offer => offer.kind === "seller_counteroffer",
  );
  const highestOpenSellerCounterofferAccepted =
    isAcceptingFinalOffers &&
    sellerCounteroffer &&
    last(
      sortBy(
        sellerCounteroffer.filter(offer =>
          LIVE_OFFER_STATES.includes(offer.state),
        ),
        offer => Number(offer.amount),
      ),
    );
  const highestOpenBuyerOffer =
    buyerOffers &&
    last(
      sortBy(
        buyerOffers.filter(offer => LIVE_OFFER_STATES.includes(offer.state)),
        offer => Number(offer.amount),
      ),
    );
  const highestOpenBuyerOfferAmount = !!highestOpenBuyerOffer
    ? Number(highestOpenBuyerOffer?.amount)
    : !!highestOpenSellerCounterofferAccepted
    ? Number(highestOpenSellerCounterofferAccepted?.amount)
    : 0;
  const offersCount = reduce(
    offerChains,
    (acc, cur) => acc + cur.offerChain.length,
    0,
  );

  const canPlaceFinalOffers =
    user &&
    ((user.isAAI && !is_owner) ||
      offers.some(offer => offer.buyer_id === user.userId));

  return (
    <>
      {isAAIVerificationModalOpen && (
        <AAIVerificationModal
          onClose={() => setIsAAIVerificationModalOpen(false)}
        />
      )}
      {isShowAllOffersModalOpen && (
        <Modal
          open
          title="Offer History"
          onClose={() => setIsShowAllOffersModalOpen(false)}
          maxWidth="sm"
          fullWidth
          className={classes.allOffersModal}
        >
          <OfferList
            maxOfferChainsToDisplay={undefined}
            listingId={listingId}
          />
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="text"
              onClick={() => setIsShowAllOffersModalOpen(false)}
            >
              Close Offer List
            </Button>
          </Box>
        </Modal>
      )}
      {isMakeOfferModalOpen && (
        <MakeOfferModal
          open
          onClose={() => setIsMakeOfferModalOpen(false)}
          buyNowPrice={buyNowPrice}
          dollarAge={dollar_age}
          highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
          isAcceptingFinalOffers={isAcceptingFinalOffers}
          isAcceptingFinalOffersEnabled={accept_final_offers}
          isMarketplaceMedianDisabled={!marketplace_median}
          listingId={listing.id}
          listingProxyIncrementAmount={parseNumber(
            proxy_increment_amount.toString(),
            2,
          )}
          lte={lifetime_amount}
          ltm={ltm}
          marketplaceMedian={marketplace_median}
          marketplaceMedianMultiplier={marketplaceMedianMultiplier}
          marketplaceMedianSource={marketplace_median_source}
          minimumPrice={Number(minimum_price)}
          offers={offers}
          openProxy={proxy}
          termExpirationDate={term_expiration}
          trackList={track_list}
          userActiveOffer={userActiveOffer}
          valuationId={valuationId}
        />
      )}
      {isLogInRequiredModalOpen && (
        <LogInRequiredModal
          open
          onClose={() => setIsLogInRequiredModalOpen(false)}
        />
      )}
      {isVerificationModalOpen && (
        <VerificationModal
          open
          onClose={() => setIsVerificationModalOpen(false)}
        />
      )}
      {isAcceptOfferModalOpen && (
        <AcceptOfferModal
          isAcceptingFinalOffersEnabled={accept_final_offers}
          isOwner={is_owner}
          listingId={listing.id}
          offer={acceptingOffer}
          onClose={() => setIsAcceptOfferModalOpen(false)}
          resaleInfo={resaleInfo}
          royxTake={royxTake}
        />
      )}
      {isRejectOfferModalOpen && (
        <RejectOfferModal
          onClose={() => setIsRejectOfferModalOpen(false)}
          listingId={listingId}
          isOwner={is_owner}
          offer={rejectingOffer}
          resaleInfo={resaleInfo}
          royxTake={royxTake}
        />
      )}
      {isMakeCounterofferModalOpen && (
        <MakeCounterofferModal
          buyNowPrice={buyNowPrice}
          highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
          isAcceptingFinalOffersEnabled={accept_final_offers}
          isOwner={is_owner}
          listingId={listingId}
          ltm={ltm}
          minimumPrice={Number(minimum_price)}
          offer={counteringOffer}
          offers={offers}
          onClose={() => setIsMakeCounterofferModalOpen(false)}
          onRejectOffer={(offer: ListingOffer) => {
            setIsMakeCounterofferModalOpen(false);
            handleOpenRejectOfferModal(offer);
          }}
          resaleInfo={resaleInfo}
          royxTake={royxTake}
          valuationId={valuationId}
        />
      )}
      {isEditOfferModalOpen && (
        <EditOfferModal
          onClose={() => setIsEditOfferModalOpen(false)}
          buyNowPrice={buyNowPrice}
          dollarAge={dollar_age}
          highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
          listingId={listingId}
          listingProxyIncrementAmount={proxy_increment_amount}
          lte={lifetime_amount}
          ltm={ltm}
          marketplaceMedian={marketplace_median}
          marketplaceMedianMultiplier={marketplaceMedianMultiplier}
          marketplaceMedianSource={marketplace_median_source}
          offer={editingOffer}
          offers={offers}
          openProxy={proxy}
          termExpiration={term_expiration}
          trackList={track_list}
          valuationId={valuationId}
        />
      )}
      {isWithdrawOfferModalOpen && (
        <WithdrawOfferModal
          onClose={() => setIsWithdrawOfferModalOpen(false)}
          listingId={listingId}
          offer={withdrawingOffer}
        />
      )}
      {!is_owner && isAcceptingFinalOffers && (
        <Box mb={2} textAlign="center">
          <Button
            customColor="green"
            variant="contained"
            size="large"
            style={{ width: "40%" }}
            onClick={
              !user
                ? () => setIsLogInRequiredModalOpen(true)
                : canPlaceFinalOffers
                ? () => setIsMakeOfferModalOpen(true)
                : () => setIsAAIVerificationModalOpen(true)
            }
            id="make-offer"
          >
            Bid Now
          </Button>
        </Box>
      )}
      {displayProxyRow && (
        <Box my={2}>
          {!!proxy && (
            <>
              <Typography
                variant="h3"
                className={classes.sectionTitle}
                gutterBottom
              >
                Your Proxy
              </Typography>
              <ProxyRow
                marketplaceMedian={marketplace_median}
                marketplaceMedianMultiplier={marketplaceMedianMultiplier}
                marketplaceMedianSource={marketplace_median_source}
                buyNowPrice={buyNowPrice}
                highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
                isMarketplaceMedianDisabled={!marketplace_median}
                listingId={listingId}
                listingProxyIncrementAmount={proxy_increment_amount}
                ltm={ltm}
                userHasActiveOffer={!!userActiveOffer}
              />
            </>
          )}
        </Box>
      )}
      {displayTitle && !!offers.length && (
        <Typography variant="h3" className={classes.sectionTitle} gutterBottom>
          Offer History
        </Typography>
      )}
      {!offers.length && !is_owner && !hidden && !isExpired && (
        <Box mt={2} display="flex" justifyContent="center">
          <Button
            customColor="green"
            variant="contained"
            size="large"
            onClick={
              hasBuyListingsPermission
                ? () => setIsMakeOfferModalOpen(true)
                : user
                ? () => setIsVerificationModalOpen(true)
                : () => setIsLogInRequiredModalOpen(true)
            }
            disabled={!!userActiveOffer}
            id="make-offer"
          >
            Make First Offer
          </Button>
        </Box>
      )}
      {displayOfferChains.map(
        ({ offerChain, hasActiveOffer }, offerChainIndex) =>
          offerChain.map((offer: ListingOffer, offerIndex: number) => {
            return (
              <OfferRow
                key={`${offerChainIndex}-${offerIndex}`}
                listingId={listingId}
                offer={offer}
                offerIndex={offerChain.length - offerIndex - 1}
                isActive={hasActiveOffer}
                isAssetOwner={is_owner}
                setIsAcceptOfferModalOpen={() =>
                  handleOpenAcceptOfferModal(offer)
                }
                setIsRejectOfferModalOpen={() =>
                  handleOpenRejectOfferModal(offer)
                }
                setIsCounterofferModalOpen={() =>
                  handleOpenCounterofferModal(offer)
                }
                setIsEditOfferModalOpen={() => handleOpenEditOfferModal(offer)}
                setIsWithdrawOfferModalOpen={() =>
                  handleOpenWithdrawOfferModal(offer)
                }
              />
            );
          }),
      )}

      {offers.length > displayedOffersCount && (
        <Box display="flex" justifyContent="center">
          <Button onClick={() => setIsShowAllOffersModalOpen(true)}>
            Click to see all Offers ({offersCount})
          </Button>
        </Box>
      )}
    </>
  );
};

export default OfferList;
